import * as env from '@/environment'
import {
  getAccessToken,
  getCompanyNameKey,
  getSubdomain,
  revokeActiveSession,
} from '@/service/activeSession'
import { initAppLauncher } from '@attensi/app-launcher'
import { redirect } from '@attensi/redirect'
import {
  accessTokenToTransferKey,
  getSessions,
  removeSession,
} from '@attensi/session'

export async function redirectToLogin() {
  redirect.toLogin({
    config: {
      subdomain: await getSubdomain(),
    },
    redirect_url: window.location.origin + window.location.pathname,
    login_url: env.LOGIN_URL,
    reset: true,
    pick_company: true,
    transfer_key: true,
  })
}

export async function revokeAndRedirectToLogin() {
  await revokeActiveSession()
  await redirectToLogin()
}

export async function redirectToLogout() {
  const subdomain = await getSubdomain()

  if (subdomain) {
    await removeSession(subdomain)
  }

  const nextSessions = await getSessions()
  if (nextSessions.length > 0) {
    // Access token is not revoked when not using login service to log out
    const nextSession = nextSessions[0]
    window.location.href = `${location.origin}/${nextSession.subdomain}`
    return
  }

  redirect.toLogin({
    config: {
      subdomain,
      access_token: await getAccessToken(), // TODO: Don't do this
    },
    logout: true,
    redirect_url: location.origin,
    login_url: env.LOGIN_URL,
    pick_company: true,
    reset: true,
    transfer_key: true,
  })
}

export async function redirectToPortal(params: { open_in_new_tab: boolean }) {
  const accessToken = await getAccessToken()
  const transferKey = accessToken
    ? await accessTokenToTransferKey(accessToken)
    : undefined

  redirect.to({
    config: {
      subdomain: await getSubdomain(),
    },
    href: env.PORTAL_URL,
    open_in_new_tab: params.open_in_new_tab,
    transfer_key: transferKey,
  })
}

export async function redirectAuthenticated({
  url,
  newTab = false,
  redirectPath,
}: {
  url: string
  newTab?: boolean
  redirectPath?: string
}) {
  const accessToken = await getAccessToken()
  const transferKey = accessToken
    ? await accessTokenToTransferKey(accessToken)
    : undefined

  const authenticatedUrl = redirect.createUrl({
    config: {
      subdomain: await getSubdomain(),
      company_name_key: await getCompanyNameKey(),
    },
    href: url,
    transfer_key: transferKey,
  })

  redirect.to({
    href: redirectPath
      ? `${authenticatedUrl}&redirect_path=${encodeURIComponent(redirectPath)}`
      : authenticatedUrl,
    open_in_new_tab: newTab,
  })
}

export async function pickCompany() {
  redirect.toLogin({
    redirect_url: window.location.origin,
    login_url: env.LOGIN_URL,
    reset: true,
    pick_company: true,
    transfer_key: true,
  })
}

export const initializeAppLauncher = async () => {
  const accessToken = await getAccessToken()
  const subdomain = await getSubdomain()
  const companyNameKey = await getCompanyNameKey()

  initAppLauncher({
    env: env.APP_ENV,
    apiUrl: env.API_BASE_URL,
    authUrl: env.AUTH_URL,
    apiToken: env.API_TOKEN,
    accessToken: accessToken!,
    subdomain: subdomain!,
    companyNameKey: companyNameKey!,
  })
}
